import {
  useGetFindingsQuery,
  usePackageIdOrNull,
  useProjectIdOrNull,
  useServiceNameOrNull,
} from '@consigli/hooks';
import { ConflictType, ServiceName } from '@consigli/types';
import { skipToken } from '@reduxjs/toolkit/query';

import { rfpFindingsQuery } from '@/pages/insight/dashboard/rfp/rfp-findings-query';

import { type AddContextMenuKind } from '../types';
import { isListedRequirement } from './response-requirements';
import { isOfferedRfpFinding } from './use-rfp-findings-list';

/**
 * The RFP-only rows the add-context menu offers on this package: milestones and
 * response requirements, each only when the package has one the modal would list.
 *
 * The requests are the RFP dashboard's own, so RTK Query answers from its cache
 * when the dashboard has already loaded them and fetches once when it has not.
 * Outside an RFP Review package nothing is requested and nothing is offered.
 */
export const useRfpContextKinds = (): AddContextMenuKind[] => {
  const projectId = useProjectIdOrNull();
  const packageId = usePackageIdOrNull();
  const isRfpPackage = useServiceNameOrNull() === ServiceName.RFP_RFQ_REVIEW;
  const isQueryable = isRfpPackage && projectId !== null && packageId !== null;

  const { currentData: milestones } = useGetFindingsQuery(
    isQueryable ? rfpFindingsQuery(projectId, packageId, ConflictType.MILESTONE) : skipToken,
  );
  const { currentData: deliverables } = useGetFindingsQuery(
    isQueryable ? rfpFindingsQuery(projectId, packageId, ConflictType.DELIVERABLE) : skipToken,
  );

  if (!isQueryable) return [];

  // Offered on the same terms the modal lists them by, so a row never opens
  // onto an empty list.
  const kinds: AddContextMenuKind[] = [];
  if (milestones?.results.some(isOfferedRfpFinding)) kinds.push('milestone');
  if (
    deliverables?.results.some(
      (finding) => isOfferedRfpFinding(finding) && isListedRequirement(finding),
    )
  ) {
    kinds.push('response-requirement');
  }
  return kinds;
};
